import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { DeskEmpty, Eyebrow, SectionHead, dateLong, dateShort, daysUntil } from './essayShared';

// Everything with a date on it: what falls due this week, then the application
// deadlines themselves.
export default function SharedDeadlinesPage() {
  const { grant, grantId, desk } = useOutletContext();
  const { weekAhead, nextDeadline, essays } = desk;

  // Every school on the list, from the essays that are linked to one.
  const schools = [];
  const seen = new Set();
  essays.forEach((essay) => {
    if (!essay.deadline || !essay.school || seen.has(essay.school)) return;
    seen.add(essay.school);
    schools.push({ name: essay.school, deadline: essay.deadline });
  });
  schools.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 32px 64px' }}>
      <Eyebrow>What is coming</Eyebrow>
      <h1
        className="font-serif-editorial"
        style={{ fontSize: 44, fontWeight: 400, lineHeight: 1, margin: '0 0 8px', color: 'var(--college-ink)' }}
      >
        Deadlines
      </h1>
      {nextDeadline && (
        <p style={{ margin: '0 0 28px', fontSize: 14, color: 'var(--college-ink-soft)' }}>
          Next up: <strong>{nextDeadline.collegeName}</strong> {nextDeadline.planLabel || nextDeadline.plan} —{' '}
          <span className="font-mono-editorial">{dateLong(nextDeadline.deadline)}</span>, {nextDeadline.days} days away.
        </p>
      )}

      <div style={{ marginTop: 12 }}>
        <SectionHead title="The next seven days" size={24} meta={`${weekAhead.length} item${weekAhead.length === 1 ? '' : 's'}`} />
        <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)', borderTop: 'none' }}>
          {weekAhead.length === 0 ? (
            <p style={{ margin: 0, padding: '18px', fontSize: 13, color: 'var(--college-muted)' }}>
              Nothing due in the next seven days.
            </p>
          ) : (
            weekAhead.map((item, i) => (
              <div
                key={`${item.label}-${i}`}
                style={{
                  display: 'flex',
                  gap: 16,
                  padding: '14px 18px',
                  borderBottom: i === weekAhead.length - 1 ? 'none' : '1px solid var(--college-border-soft)',
                }}
              >
                <span
                  className="font-mono-editorial"
                  style={{ fontSize: 12.5, minWidth: 70, color: i === 0 ? 'var(--college-red)' : 'var(--college-ink-soft)' }}
                >
                  {dateShort(item.date)}
                </span>
                <span style={{ fontSize: 13.5, color: 'var(--college-ink)' }}>{item.label}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={{ marginTop: 36 }}>
        <SectionHead
          title="Application deadlines"
          size={24}
          right={
            grant.seeProgress ? (
              <Link to={`/shared/${grantId}/list`} style={{ fontSize: 12.5, color: 'var(--college-red)', textDecoration: 'none' }}>
                Open the list →
              </Link>
            ) : null
          }
        />
        {schools.length === 0 ? (
          <div style={{ marginTop: 16 }}>
            <DeskEmpty title="No dated schools yet" body={`${grant.ownerName} hasn't linked an essay to a school with a deadline.`} />
          </div>
        ) : (
          <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)', borderTop: 'none' }}>
            {schools.map((school, i) => (
              <div
                key={school.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: '14px 18px',
                  borderBottom: i === schools.length - 1 ? 'none' : '1px solid var(--college-border-soft)',
                }}
              >
                <span className="font-serif-editorial" style={{ fontSize: 17, color: 'var(--college-ink)', flex: 1 }}>
                  {school.name}
                </span>
                <span className="font-mono-editorial" style={{ fontSize: 12.5, color: 'var(--college-ink-soft)' }}>
                  {dateLong(school.deadline)} · {daysUntil(school.deadline)}d
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
